import React, { Component } from 'react';
import { Theme } from '../Theme';

export interface MenuItemProps {
    label: string;
    icon: {
        normal: string;
        selected: string;
    };
    id: string;
    onSelect: () => void;
    isDevelopment?: boolean; // development areas retain the yellow restricted-area tint
}

export interface MenuProps {
    options: Array<MenuItemProps>,
    selectedMenuOptionIndex: number,
    onLogout: () => void,
}

interface State {
    selectedOptionIndex: number;
    collapsed: boolean;
}

const EXPANDED_WIDTH = '260px';
const COLLAPSED_WIDTH = '68px';

export class Menu extends Component<MenuProps, State> {

    constructor(props: MenuProps) {
        super(props);
        this.state = {
            selectedOptionIndex: this.props.selectedMenuOptionIndex,
            collapsed: false,
        };
        console.log(`MENU: constructor()`);
    }

    componentDidUpdate(previousProps: MenuProps) {
        // Provider access changes can move the selected item within the visible menu.
        if (previousProps.selectedMenuOptionIndex !== this.props.selectedMenuOptionIndex &&
            this.state.selectedOptionIndex !== this.props.selectedMenuOptionIndex) {
            this.setState({ selectedOptionIndex: this.props.selectedMenuOptionIndex });
        }
    }

    renderMenuItem(index: number, item: MenuItemProps) {
        const isSelected = this.state.selectedOptionIndex === index;
        const { collapsed } = this.state;
        let baseStyle: React.CSSProperties = isSelected
            ? (collapsed ? styles.menuItemSelectedCollapsed : styles.menuItemSelected)
            : (collapsed ? styles.menuItemCollapsed : styles.menuItem);
        // Keep development-only options visually distinct.
        if (item.isDevelopment) {
            baseStyle = isSelected
                ? { ...baseStyle, backgroundColor: Theme.Colours.superHighlightSelected, color: Theme.Colours.text }
                : {
                    ...baseStyle,
                    backgroundColor: Theme.Colours.superHighlight,
                    borderRadius: '6px',
                    // Match the selected item's narrower width so the tint keeps symmetric left/right margins.
                    ...(collapsed ? {} : { width: '210px' }),
                };
        }
        return (
            <div key={index}
                style={baseStyle}
                onClick={() => {
                    this.setState({ selectedOptionIndex: index } );
                    item.onSelect();
                }}
                title={collapsed ? item.label : undefined}
            >
                <img width={24} height={24} src={isSelected ? item.icon.selected : item.icon.normal} alt={item.label} style={{ flexShrink: 0 }} />
                {!collapsed && item.label}
            </div>
        );
    }

    render(): React.ReactNode {
        const { collapsed } = this.state;
        const containerStyle = collapsed ? styles.containerCollapsed : styles.container;

        return (
            <div style={containerStyle}>

                {/* top section */}
                {!collapsed && <img style={styles.logo} src={require('./assets/superpenguin-logo.webp')} alt='SuperPenguin' />}
                {collapsed && <div style={styles.logoCollapsed} />}
                {this.props.options.map((option, index) => this.renderMenuItem(index, option))}

                <div style={styles.menuSeparator} />

                {/* bottom section */}
                <div style={collapsed ? styles.menuItemCollapsed : styles.menuItem} onClick={() => {
                    this.props.onLogout();
                }}
                    title={collapsed ? 'Logout' : undefined}
                >
                    <img width={24} height={24} src={require('./assets/icons/logout_normal.svg').default} alt={'Logout'} style={{ flexShrink: 0 }} />
                    {!collapsed && 'Logout'}
                </div>

                {/* collapse toggle */}
                <div
                    style={styles.toggleButton}
                    onClick={() => this.setState({ collapsed: !collapsed })}
                >
                    {collapsed ? '\u203A' : '\u2039'}
                </div>

            </div>
        );
    }
};

const containerBase = {
    flexDirection: 'column' as 'column',
    height: '100vh',
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '35px 0px 0px 0px', // top right bottom left
    alignItems: 'flex-start',
    backgroundColor: Theme.Colours.background,
    color: Theme.Colours.text,
    border: Theme.Colours.border,
    overflow: 'hidden' as const,
    transition: 'width 0.2s ease, min-width 0.2s ease',
};

const menuItemBase = {
    height: '35px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
};

const styles = {
    container: {
        ...containerBase,
        width: EXPANDED_WIDTH,
        minWidth: EXPANDED_WIDTH,
    },
    containerCollapsed: {
        ...containerBase,
        width: COLLAPSED_WIDTH,
        minWidth: COLLAPSED_WIDTH,
    },
    logo: {
        // Same 54px slot as the collapsed placeholder, so menu items do not shift on toggle.
        width: '200px',
        height: '54px',
        objectFit: 'contain' as const,
        objectPosition: 'left center',
        margin: '0px 0px 56px 26px', // top right bottom left
    },
    logoCollapsed: {
        height: '54px',
        margin: '0px 0px 56px 0px',
    },
    menuItem: {
        ...menuItemBase,
        width: '230px',
        margin: '8px 16px 8px 16px',
        padding: '0px 8px 0px 8px', // top right bottom left
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
    },
    menuItemSelected: {
        ...menuItemBase,
        width: '210px',
        margin: '8px 16px 8px 16px',
        padding: '0px 8px 0px 8px', // top right bottom left
        backgroundColor: Theme.Colours.buttonBackground,
        color: Theme.Colours.buttonText,
        borderRadius: '6px',
        fontWeight: 'bold' as 'bold',
    },
    menuItemCollapsed: {
        ...menuItemBase,
        margin: '8px auto',
        padding: '0px 8px',
        justifyContent: 'center' as const,
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
    },
    menuItemSelectedCollapsed: {
        ...menuItemBase,
        margin: '8px auto',
        padding: '0px 8px',
        justifyContent: 'center' as const,
        backgroundColor: Theme.Colours.buttonBackground,
        color: Theme.Colours.buttonText,
        borderRadius: '6px',
        fontWeight: 'bold' as 'bold',
    },
    logoutButton: {
        margin: '0px 0px 0px 26px', // top right bottom left
        padding: '0px 0px 0px 0px', // top right bottom left
        cursor: 'pointer',
        color: Theme.Colours.text,
    },
    menuSeparator: {
        flex: 1,
    },
    toggleButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        flexShrink: 0,
        alignSelf: 'flex-end' as const,
        margin: '8px 16px 50px 0px',
        cursor: 'pointer',
        color: Theme.Colours.text,
        fontSize: '20px',
        borderRadius: '6px',
        border: Theme.Colours.border,
        userSelect: 'none' as const,
        boxSizing: 'border-box' as const,
    },
};
